import React, { useEffect, useState } from "react";
import { C, ROLE_HIERARCHY, ROLE_LABELS } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function UsersAdminPage({ showNotification, authStatus }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Substep 5-κ.3: firm locations for the per-row dropdown
  const [locations, setLocations] = useState([]);

  const currentFirmUserId = authStatus?.firm_user?.id;

  const load = () => {
    setError(null);
    Promise.all([
      apiFetch("/api/users"),
      apiFetch("/api/firm-locations?active_only=true").catch(() => ({ data: [] })),
    ])
      .then(([data, locs]) => {
        setUsers(data.data || data.users || []);
        setLocations(locs.data || []);
      })
      .catch(e => {
        setError(e.message || "Failed to load users");
        setUsers([]);
      });
  };
  useEffect(load, []);

  const updateUser = async (userId, body) => {
    setSavingId(userId);
    try {
      // PUT returns the updated row fields at top level (id, email,
      // name, role, is_active, changes_applied).
      const updated = await apiFetch(`/api/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setUsers(prev => prev.map(u => (u.id === userId ? { ...u, ...updated } : u)));
      showNotification && showNotification("User updated", "success");
    } catch (e) {
      // 403 body has { detail: { error, message, ... } } — apiFetch
      // flattens that into Error(message).
      showNotification && showNotification(e.message || "Update failed", "error");
      // Reload so the dropdown reverts to the actual server value
      load();
    } finally {
      setSavingId(null);
    }
  };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1200px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "24px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" },
      }, "Users & Roles"),
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted, lineHeight: 1.5, maxWidth: "780px" } },
        "Everyone who has signed in via Clio shows up here. New users start as ",
        React.createElement("strong", null, "Pending"),
        " — promote them to Paralegal, Attorney, Partner, or SuperAdmin to unlock features. ",
        "Bootstrap SuperAdmins are pinned via the ",
        React.createElement("code", { style: { fontSize: "12px", background: C.warmGray, padding: "1px 6px", borderRadius: "4px" } },
          "SUPERADMIN_CLIO_USER_IDS"),
        " environment variable.",
      ),
    ),

    error && React.createElement("div", {
      style: {
        background: C.errorBg, color: C.error, padding: "12px 16px",
        borderRadius: "10px", marginBottom: "16px", fontSize: "14px",
        border: `1px solid ${C.error}30`,
      },
    }, error),

    users === null
      ? React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null))
      : users.length === 0
        ? React.createElement("div", {
            style: { padding: "32px", textAlign: "center", color: C.textMuted, background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}` },
          }, "No users found.")
        : React.createElement("div", {
            style: { background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}`, overflow: "hidden" },
          },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "14px" } },
              React.createElement("thead", null,
                React.createElement("tr", { style: { background: C.cream, borderBottom: `1px solid ${C.warmGray}` } },
                  ["User", "Role", "Location", "Status", "Last Login", "Created"].map(h =>
                    React.createElement("th", {
                      key: h,
                      style: {
                        padding: "12px 16px", textAlign: "left", fontSize: "11px",
                        fontWeight: 700, color: C.textMuted, textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      },
                    }, h),
                  ),
                ),
              ),
              React.createElement("tbody", null,
                users.map(u => {
                  const isSelf = u.id === currentFirmUserId;
                  const saving = savingId === u.id;
                  return React.createElement("tr", {
                    key: u.id,
                    style: { borderBottom: `1px solid ${C.warmGray}` },
                  },
                    // User cell
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top" } },
                      React.createElement("div", { style: { fontWeight: 600, color: C.navy, marginBottom: "2px" } },
                        u.name || "(no name)",
                        isSelf && React.createElement("span", {
                          style: { marginLeft: "8px", fontSize: "10px", fontWeight: 700, color: C.gold, letterSpacing: "0.05em" },
                        }, "(YOU)"),
                      ),
                      React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } }, u.email || "—"),
                      React.createElement("div", { style: { fontSize: "11px", color: C.textLight, marginTop: "2px" } },
                        "Clio ID: ", u.clio_user_id),
                    ),
                    // Role cell — dropdown
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top" } },
                      React.createElement("select", {
                        value: u.role,
                        disabled: saving || isSelf,
                        title: isSelf ? "You can't change your own role." : "Change role tier",
                        onChange: e => updateUser(u.id, { role: e.target.value }),
                        style: {
                          padding: "6px 10px", borderRadius: "8px", fontSize: "13px",
                          border: `1px solid ${C.warmGray}`, background: isSelf ? C.cream : "#fff",
                          color: isSelf ? C.textMuted : C.navy, cursor: isSelf ? "not-allowed" : "pointer",
                          minWidth: "130px",
                        },
                      },
                        ROLE_HIERARCHY.map(r =>
                          React.createElement("option", { key: r, value: r }, ROLE_LABELS[r] || r),
                        ),
                      ),
                    ),
                    // Location cell — Substep 5-κ.3. Admin override
                    // of attorney's location. Empty option means "no
                    // assignment; falls back to firm default."
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top" } },
                      React.createElement("select", {
                        value: u.location_id || "",
                        disabled: saving,
                        title: "Attorney's office location — affects letterhead on documents they generate",
                        onChange: e => {
                          const raw = e.target.value;
                          updateUser(u.id, {
                            location_id: raw === "" ? null : parseInt(raw, 10),
                          });
                        },
                        style: {
                          padding: "6px 10px", borderRadius: "8px", fontSize: "13px",
                          border: `1px solid ${C.warmGray}`, background: "#fff",
                          color: C.navy, cursor: "pointer", minWidth: "150px",
                        },
                      },
                        React.createElement("option", { value: "" }, "(default)"),
                        ...locations.map(loc => React.createElement("option", { key: loc.id, value: loc.id },
                          loc.name,
                        )),
                      ),
                    ),
                    // Status cell — toggle
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top" } },
                      React.createElement("button", {
                        disabled: saving || isSelf,
                        title: isSelf ? "You can't deactivate yourself." : (u.is_active ? "Deactivate user" : "Activate user"),
                        onClick: () => updateUser(u.id, { is_active: !u.is_active }),
                        style: {
                          padding: "5px 12px", borderRadius: "9999px", fontSize: "12px",
                          fontWeight: 600, letterSpacing: "0.03em",
                          background: u.is_active ? C.successBg : C.errorBg,
                          color: u.is_active ? C.success : C.error,
                          border: `1px solid ${u.is_active ? C.success : C.error}30`,
                          cursor: (saving || isSelf) ? "not-allowed" : "pointer",
                          opacity: (saving || isSelf) ? 0.6 : 1,
                        },
                      }, u.is_active ? "Active" : "Disabled"),
                    ),
                    // Last login
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top", fontSize: "13px", color: C.textMuted } },
                      u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—",
                    ),
                    // Created
                    React.createElement("td", { style: { padding: "14px 16px", verticalAlign: "top", fontSize: "13px", color: C.textMuted } },
                      u.created_at ? new Date(u.created_at).toLocaleDateString() : "—",
                    ),
                  );
                }),
              ),
            ),
          ),

    // Role legend
    React.createElement("div", {
      style: { marginTop: "24px", padding: "16px 20px", background: "#fff",
               borderRadius: "12px", border: `1px solid ${C.warmGray}`, fontSize: "13px", color: C.textMuted, lineHeight: 1.7 },
    },
      React.createElement("div", { style: { fontWeight: 700, color: C.navy, marginBottom: "8px", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.06em" } },
        "Role tiers (low → high)"),
      React.createElement("div", null, React.createElement("strong", null, "Pending"), " — Can sign in but has no permissions until promoted."),
      React.createElement("div", null, React.createElement("strong", null, "Paralegal"), " — Daily paperwork: single doc generation, AI extractions, provider mgmt."),
      React.createElement("div", null, React.createElement("strong", null, "Attorney"), " — High-stakes operations: PACER search, SOL compute, push-to-Clio, matter creation, batch generate."),
      React.createElement("div", null, React.createElement("strong", null, "Partner"), " — Firm management: template sweep + audit log access."),
      React.createElement("div", null, React.createElement("strong", null, "SuperAdmin"), " — Full control: integrations, templates, user management, webhooks."),
    ),
  );
}


